import { useMemo, useState } from 'react'
import {
  Icon,
  Box,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  VStack,
  Flex,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { AiOutlineSearch } from 'react-icons/ai'
import { get } from 'lodash'

import { Can } from '@/features/permissions/components'
import { ConsentCsvDownload, ConsentTable } from '../../components'

import { useDebounce } from '@/lib/hooks'

import { PollingStatus } from '../../../types'

import { useFilterContext } from '@/features/filters/context'
import { useMemoDisplayDashboard } from '@/lib/hooks'
import { useConsents } from '../../queries'
import { periodOptions } from '../constants'
import { MonthPicker } from '@/components/MonthPicker'

type ConsentLogContainerProps = {
  project?: Project
  downloadStatus: PollingStatus
  hasConsent: boolean
}

export const ConsentLogContainer = (props: ConsentLogContainerProps) => {
  const { project, downloadStatus, hasConsent } = props
  const [isCustomPeriod, setIsCustomPeriod] = useState(false)

  const { t } = useTranslation('domainConsent')
  const { t: tCommon } = useTranslation('common')

  const { setFilter, meta } = useFilterContext()

  const debounceHandleChange = useDebounce((value: string) => {
    setFilter('search', value)
  })

  const isDisplayDashboard = useMemoDisplayDashboard()

  const { data: consents } = useConsents({
    enabled: isDisplayDashboard,
  })

  const rememberChoices: Record<string, number> = get(
    consents,
    'meta.remember_choices',
    {},
  )
  const category: Record<string, number> = get(
    consents,
    'meta.consent_categories',
    {},
  )

  const formatPeriodOptions = useMemo(() => {
    return periodOptions.map(({ label, value }) => (
      <option value={value} key={`period-${value}`}>
        {t(label)}
      </option>
    ))
  }, [periodOptions, t])

  return (
    <Box id="dashboard-log-section">
      <VStack spacing="4" alignItems="flex-start" w="full">
        <HStack w="full">
          <Text fontWeight="bold">{t('consentLog')}</Text>
          <HStack ml="auto !important">
            {project && hasConsent && (
              <Can I="export" a="consent">
                {() => (
                  <ConsentCsvDownload
                    project={project}
                    downloadStatus={downloadStatus}
                  />
                )}
              </Can>
            )}
          </HStack>
        </HStack>

        <Flex justifyContent="space-between" w="full">
          <HStack spacing="4" alignItems="flex-start">
            <VStack alignItems="flex-start">
              <Text fontWeight="medium" color="gray.900" h={6}>
                {t('filter.search')}
              </Text>
              <InputGroup sx={{ width: '240px' }} size="sm">
                <InputLeftElement pointerEvents="none">
                  <Icon as={AiOutlineSearch} color="gray.600" />
                </InputLeftElement>
                <Input
                  placeholder={tCommon('search')}
                  onChange={(e) => debounceHandleChange(e.target.value)}
                />
              </InputGroup>
            </VStack>

            <VStack alignItems="flex-start">
              <Text fontWeight="medium" color="gray.900" h={6}>
                {t('filter.action')}
              </Text>
              <Select
                size="sm"
                onChange={(e) => {
                  setFilter('remember_choice', e.target.value)
                }}
                sx={{ w: '240px' }}
                id="consent-action-select"
              >
                <option value="">{tCommon('all')}</option>
                {Object.entries(rememberChoices).map(([key, value]) => (
                  <option value={value} key={value}>
                    {t(`acceptedStatus.${key}`)}
                  </option>
                ))}
              </Select>
            </VStack>

            <VStack alignItems="flex-start">
              <Text fontWeight="medium" color="gray.900" h={6}>
                {t('filter.category')}
              </Text>
              <Select
                size="sm"
                onChange={(e) => {
                  setFilter('category_id', e.target.value)
                }}
                sx={{ w: '240px' }}
                id="consent-action-select"
              >
                <option value="">{tCommon('all')}</option>
                {Object.entries(category).map(([key, value]) => (
                  <option value={value} key={value}>
                    {key}
                  </option>
                ))}
              </Select>
            </VStack>

            <VStack alignItems="flex-start">
              <Text fontWeight="medium" color="gray.900" h={6}>
                {t('filter.period')}
              </Text>
              <Select
                size="sm"
                onChange={(e) => {
                  if (e.currentTarget.value !== 'custom') {
                    setIsCustomPeriod(false)
                    setFilter('period', e.currentTarget.value)
                  } else {
                    setIsCustomPeriod(true)
                  }
                }}
                sx={{ w: '240px' }}
                id="consent-action-select"
              >
                {formatPeriodOptions}
              </Select>
            </VStack>

            {isCustomPeriod && (
              <VStack alignItems="flex-start">
                <Text fontWeight="medium" color="gray.900" h={6}>
                  {t('filter.custom')}
                </Text>
                <MonthPicker
                  selected={meta.custom_period || ''}
                  onChange={(e) => {
                    setFilter('period', 'custom')
                    setFilter(
                      'custom_period',
                      `${e.getMonth() + 1}/${e.getFullYear()}`,
                    )
                  }}
                  size="sm"
                />
              </VStack>
            )}
          </HStack>
        </Flex>
      </VStack>

      <Box mt="4">
        <ConsentTable />
      </Box>
    </Box>
  )
}
